import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _, { noop } from 'lodash';

import ListItemText from '~/components/core/Atomic/ListItemText';
import MenuItem from '~/components/core/Atomic/MenuItem';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import Checkbox from '~/components/ui/atoms/Checkbox';

import { getLobDescription } from '../../../Utils/lobUtils';
import useOrganization from '../../OrganizationContext';

import useLobOptions from './hooks/useLobOptions';

import { useStyles } from '../../../assets/styles';

export const LobMultiSelect = ({
  subOrganizationIds = [],
  onChange = noop,
  disabled = false,
  label = 'Line of Business',
  textFieldProps,
}) => {
  const { classes } = useStyles();
  const { subOrganizationEnabled } = useOrganization();
  const { lobOptions, isLoading } = useLobOptions({ subOrganizationIds });
  const { lobConfigurationsDict } = useLobConfiguration();
  const [selectedLobs, setSelectedLobs] = useState([]);

  const handleLobsChanged = useCallback(
    (lob) => {
      setSelectedLobs(lob);
      if (onChange) {
        onChange(lob);
      }
    },
    [onChange]
  );

  const handleSelectionChanged = useCallback(
    (lobs) => {
      handleLobsChanged(lobs);
    },
    [handleLobsChanged]
  );

  const isSubOrgEmpty = _.isEmpty(subOrganizationIds);

  // Keep the selection a subset of what's currently selectable. When sub-orgs are enabled
  // but none is selected nothing is selectable, so the selection collapses to empty instead
  // of lingering behind the (now disabled) field and continuing to filter invisibly.
  useEffect(() => {
    const allowedLobs = subOrganizationEnabled && isSubOrgEmpty ? [] : lobOptions;
    const intersection = _.intersection(selectedLobs, allowedLobs);
    if (selectedLobs.length !== intersection.length) {
      handleLobsChanged(intersection);
    }
  }, [lobOptions, subOrganizationEnabled, isSubOrgEmpty, selectedLobs, handleLobsChanged]);

  const isDisabled = disabled || isLoading || (subOrganizationEnabled && isSubOrgEmpty);

  const isLobChecked = (lob) => {
    return selectedLobs.includes(lob);
  };

  const renderFieldValue = (lobs) => {
    return lobs.map((lob) => getLobDescription(lob, lobConfigurationsDict)).join(', ');
  };

  return (
    <SingleSelectField
      value={selectedLobs}
      label={label}
      className={classes.textField}
      disabled={isDisabled}
      onChange={handleSelectionChanged}
      fullWidth
      SelectProps={{
        multiple: true,
        renderValue: renderFieldValue,
      }}
      {...textFieldProps}
    >
      {lobOptions.map((lob) => (
        <MenuItem key={lob} value={lob}>
          <Checkbox checked={isLobChecked(lob)} />
          <ListItemText primary={getLobDescription(lob, lobConfigurationsDict)} />
        </MenuItem>
      ))}
    </SingleSelectField>
  );
};

LobMultiSelect.propTypes = {
  subOrganizationIds: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  onChange: PropTypes.func,
  textFieldProps: PropTypes.object,
};
